import { Controller } from "@hotwired/stimulus";

// Assistant d'import des parents, en trois étapes : fichier, correspondance
// des colonnes, aperçu avant écriture.
//
// Les libellés des champs viennent du serveur (valeur `fields`) plutôt que
// d'être recopiés ici : c'est la seule façon que la correspondance parle la
// langue de l'utilisateur, et que l'ajout d'une colonne ne demande pas de
// toucher au JavaScript.
export default class extends Controller {
  static targets = [
    "step1",
    "step2",
    "step3",
    "progressStep1",
    "progressStep2",
    "progressStep3",
    "progressLine1",
    "progressLine2",
    "form",
    "fileInput",
    "sheetNameInput",
    "dropZone",
    "fileInfo",
    "fileName",
    "rowCount",
    "loadingIndicator",
    "errorMessage",
    "errorText",
    "mappingContainer",
    "mappingError",
    "mappingErrorText",
    "summaryFile",
    "summaryRows",
    "summaryParentsCreated",
    "summaryParentsLinked",
    "summaryLinksCreated",
    "dryRunLoading",
    "dryRunResult",
    "dryRunCounters",
    "dryRunRows",
    "nextButton",
    "prevButton",
    "submitButton",
    "cancelButton",
  ];

  static values = {
    currentStep: { type: Number, default: 1 },
    previewUrl: String,
    dryRunUrl: String,
    fields: Object,
    translations: Object,
  };

  // Nombre d'étapes de l'assistant. Sert aussi à borner la barre du haut.
  get lastStep() {
    return 3;
  }

  // Le contrôleur repart TOUJOURS de l'étape 1, fichier compris : Turbo peut
  // restaurer un DOM mis en cache (retour arrière) où la barre du haut est
  // déjà à l'étape 3 et où la carte « Fichier sélectionné » est affichée,
  // alors que l'état JavaScript, lui, est neuf. Sans cette remise à zéro,
  // l'écran annonce une étape et en montre une autre, et « Suivant » réclame
  // un fichier que l'utilisateur croit avoir déposé.
  connect() {
    this.currentStepValue = 1;
    this.fileData = null;
    this.analysisResult = null;
    this.updateStepVisibility();
    this.updateProgressIndicators();
    this.clearFile();
    this.hideError();
  }

  // --- Navigation ---------------------------------------------------------

  nextStep() {
    if (!this.validateCurrentStep()) return;
    if (this.currentStepValue >= this.lastStep) return;

    this.currentStepValue++;
    this.updateStepVisibility();
    this.updateProgressIndicators();

    if (this.currentStepValue === 2) this.renderColumnMappings();
    if (this.currentStepValue === 3) {
      this.updateSummary();
      this.runDryRun();
    }
  }

  previousStep() {
    if (this.currentStepValue <= 1) return;

    this.currentStepValue--;
    this.updateStepVisibility();
    this.updateProgressIndicators();
  }

  updateStepVisibility() {
    for (let step = 1; step <= this.lastStep; step++) {
      this[`step${step}Target`].classList.toggle(
        "hidden",
        step !== this.currentStepValue,
      );
    }

    // Via l'ATTRIBUT hidden et non la classe utilitaire : `.ds-btn { display }`
    // bat `.hidden` (spécificité égale, `.hidden` déclaré avant le DS), donc
    // les boutons resteraient visibles à toutes les étapes.
    const onFirstStep = this.currentStepValue === 1;
    const onLastStep = this.currentStepValue === this.lastStep;

    this.prevButtonTarget.hidden = onFirstStep;
    this.cancelButtonTarget.hidden = !onFirstStep;
    this.nextButtonTarget.hidden = onLastStep;
    this.submitButtonTarget.hidden = !onLastStep;
  }

  updateProgressIndicators() {
    for (let step = 1; step <= this.lastStep; step++) {
      const circle = this[`progressStep${step}Target`];
      const done = step <= this.currentStepValue;
      circle.classList.toggle("bg-cta", done);
      circle.classList.toggle("text-white", done);
      circle.classList.toggle("bg-gray-200", !done);
      circle.classList.toggle("text-gray-600", !done);
    }

    for (let line = 1; line < this.lastStep; line++) {
      const target = this[`progressLine${line}Target`];
      if (target) target.style.width = line < this.currentStepValue ? "100%" : "0%";
    }
  }

  validateCurrentStep() {
    if (this.currentStepValue === 1) {
      if (this.fileData) return true;

      this.dropZoneTarget.classList.add("border-red-500");
      return false;
    }

    if (this.currentStepValue === 2) return this.validateRequiredMappings();

    return true;
  }

  // Sans prénom, nom et téléphone, aucune fiche de responsable ne peut être
  // créée : on refuse d'avancer plutôt que de laisser le serveur rejeter
  // toutes les lignes une par une.
  validateRequiredMappings() {
    const missing = this.requiredFields().filter((field) => {
      const select = this.selectFor(field.key);
      return !select || select.value === "-1";
    });

    this.requiredFields().forEach((field) =>
      this.selectFor(field.key)?.classList.remove("border-red-500"),
    );

    if (missing.length === 0) {
      this.mappingErrorTarget.classList.add("hidden");
      return true;
    }

    missing.forEach((field) =>
      this.selectFor(field.key)?.classList.add("border-red-500"),
    );

    this.mappingErrorTextTarget.textContent = (
      this.translationsValue.missing_mapping || ""
    ).replace("%{fields}", missing.map((field) => field.label).join(", "));
    this.mappingErrorTarget.classList.remove("hidden");
    this.selectFor(missing[0].key)?.focus();

    return false;
  }

  requiredFields() {
    return this.sections()
      .flatMap((section) => section.fields)
      .filter((field) => field.required);
  }

  sections() {
    return Object.values(this.fieldsValue || {});
  }

  selectFor(key) {
    return this.element.querySelector(`select[name="parent_import[${key}_column]"]`);
  }

  // --- Fichier ------------------------------------------------------------

  handleDragOver(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.add("border-cta", "bg-cta-shared");
  }

  handleDragLeave(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.remove("border-cta", "bg-cta-shared");
  }

  handleDrop(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.remove("border-cta", "bg-cta-shared");

    const files = event.dataTransfer.files;
    if (files.length === 0) return;
    if (!this.validateFileType(files[0])) return;

    this.fileInputTarget.files = files;
    this.processFile(files[0]);
  }

  analyzeFile(event) {
    const file = event.target.files[0];
    if (file && this.validateFileType(file)) this.processFile(file);
  }

  validateFileType(file) {
    const xlsx =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    if (file.type === xlsx || file.name.endsWith(".xlsx")) return true;

    this.showError(this.translationsValue.file_error);
    return false;
  }

  async processFile(file) {
    this.fileData = file;
    // Le liseré rouge posé par « Suivant sans fichier » doit tomber dès qu'un
    // fichier arrive : sinon la zone reste signalée en faute une fois le
    // problème réglé.
    this.dropZoneTarget.classList.remove("border-red-500");
    this.showLoading();
    this.hideError();

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        body: formData,
        headers: { "X-CSRF-Token": this.csrfToken() },
      });
      const result = await response.json();

      if (result.error) {
        this.showError(result.error);
        this.fileData = null;
        return;
      }

      // Un classeur sans aucune ligne de données irait jusqu'au bouton
      // « Lancer l'import » et déposerait dans l'historique un rapport qui
      // n'a rien traité — sans que rien n'explique pourquoi.
      if (this.totalRows(result) === 0) {
        this.showError(this.translationsValue.empty_file);
        this.fileData = null;
        return;
      }

      this.analysisResult = result;
      this.showFileInfo(file, result);

      if (result.sheets && result.sheets.length > 0) {
        this.sheetNameInputTarget.value = result.sheets[0].name;
      }
    } catch (error) {
      console.error("Error analyzing file:", error);
      this.showError(this.translationsValue.analysis_error);
      this.fileData = null;
    }
  }

  showLoading() {
    this.toggleDropZone(false);
    this.fileInfoTarget.classList.add("hidden");
    this.loadingIndicatorTarget.classList.remove("hidden");
  }

  // Via l'ATTRIBUT hidden : `.ds-dropzone { display:flex }` bat l'utilitaire
  // `.hidden`, et la zone de dépôt restait affichée sous la carte du fichier.
  toggleDropZone(visible) {
    this.dropZoneTarget.hidden = !visible;
    this.dropZoneTarget.classList.toggle("hidden", !visible);
  }

  showFileInfo(file, result) {
    this.loadingIndicatorTarget.classList.add("hidden");
    this.toggleDropZone(false);
    this.fileInfoTarget.classList.remove("hidden");

    this.fileNameTarget.textContent = file.name;

    // Deux formes plutôt qu'une : « 1 lignes détectées » se lisait sur tout
    // fichier d'une seule ligne. Le zéro n'a pas de forme propre ici — un
    // classeur sans donnée est refusé plus haut.
    const count = this.totalRows(result);
    const template =
      count === 1
        ? this.translationsValue.rows_detected_one
        : this.translationsValue.rows_detected_other;
    this.rowCountTarget.textContent = (template || "%{count}").replace(
      "%{count}",
      count,
    );
  }

  totalRows(result) {
    if (!result || !result.sheets) return 0;

    return result.sheets.reduce((sum, sheet) => sum + (sheet.row_count || 0), 0);
  }

  showError(message) {
    this.loadingIndicatorTarget.classList.add("hidden");
    this.toggleDropZone(true);
    this.fileInfoTarget.classList.add("hidden");
    this.errorMessageTarget.classList.remove("hidden");
    this.errorTextTarget.textContent = message;
  }

  hideError() {
    this.errorMessageTarget.classList.add("hidden");
  }

  clearFile() {
    this.fileData = null;
    this.analysisResult = null;
    this.fileInputTarget.value = "";
    this.sheetNameInputTarget.value = "";
    this.toggleDropZone(true);
    this.fileInfoTarget.classList.add("hidden");
  }

  // --- Correspondance des colonnes ---------------------------------------

  renderColumnMappings() {
    const sheet = this.analysisResult?.sheets?.[0];
    if (!sheet) return;

    const container = this.mappingContainerTarget;
    container.replaceChildren();

    this.sections().forEach((section) => {
      container.appendChild(this.buildSection(section, sheet));
    });
  }

  buildSection(section, sheet) {
    const wrapper = document.createElement("div");

    const heading = document.createElement("h3");
    heading.className = "ds-section-title";
    heading.style.cssText =
      "font:700 11px var(--g4s-font-sans); text-transform:uppercase; letter-spacing:.04em; color:var(--ds-text-4); margin:0 0 12px; padding-bottom:8px; border-bottom:1px solid var(--ds-border);";
    heading.textContent = section.label;
    wrapper.appendChild(heading);

    const grid = document.createElement("div");
    grid.className = "grid grid-cols-1 md:grid-cols-2 gap-4";
    section.fields.forEach((field) => {
      grid.appendChild(this.buildField(field, sheet));
    });
    wrapper.appendChild(grid);

    return wrapper;
  }

  buildField(field, sheet) {
    const columns = sheet.columns || [];
    const suggested = sheet.suggested_mapping || {};

    const wrapper = document.createElement("div");
    wrapper.className = "ds-field";

    const label = document.createElement("label");
    label.className = "ds-field__label";
    label.textContent = field.label;
    if (field.required) {
      const star = document.createElement("span");
      star.className = "ds-field__req";
      star.textContent = "*";
      label.appendChild(star);
    } else {
      const hint = document.createElement("span");
      hint.style.cssText =
        "font:400 12px var(--g4s-font-sans); color:var(--ds-text-4); margin-left:4px;";
      hint.textContent = this.translationsValue.optional || "";
      label.appendChild(hint);
    }
    wrapper.appendChild(label);

    const select = document.createElement("select");
    select.name = `parent_import[${field.key}_column]`;
    select.className = "ds-input";
    select.appendChild(this.buildOption("-1", this.translationsValue.select_column));
    columns.forEach((column, index) => {
      const prefix = this.translationsValue.column_prefix || "";
      select.appendChild(
        this.buildOption(String(index), `${prefix} ${index + 1}: ${column}`.trim()),
      );
    });

    if (suggested[field.key] !== undefined) {
      select.value = String(suggested[field.key]);
    }
    wrapper.appendChild(select);

    if (suggested[field.key] !== undefined) {
      const badge = document.createElement("span");
      badge.className = "ds-chip ds-chip--success ds-chip--sm";
      // `align-self` : .ds-field est une colonne flex, et la pastille s'étirait
      // sur toute la largeur du champ au lieu d'épouser son texte.
      badge.style.cssText = "margin-top:6px; align-self:flex-start;";
      badge.textContent = this.translationsValue.auto_detected || "";
      wrapper.appendChild(badge);
    }

    return wrapper;
  }

  buildOption(value, text) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = text || "";
    return option;
  }

  // --- Aperçu avant import ------------------------------------------------

  updateSummary() {
    this.summaryFileTarget.textContent = this.fileData ? this.fileData.name : "-";
    this.summaryRowsTarget.textContent = this.totalRows(this.analysisResult);
    this.summaryParentsCreatedTarget.textContent = "-";
    this.summaryParentsLinkedTarget.textContent = "-";
    this.summaryLinksCreatedTarget.textContent = "-";
  }

  async runDryRun() {
    if (!this.hasDryRunUrlValue || !this.fileData) return;

    this.dryRunLoadingTarget.classList.remove("hidden");
    this.dryRunResultTarget.classList.add("hidden");

    const formData = new FormData();
    formData.append("file", this.fileData);
    formData.append("sheet_name", this.sheetNameInputTarget.value || "");
    this.element
      .querySelectorAll('select[name^="parent_import["]')
      .forEach((select) => {
        const field = select.name.replace("parent_import[", "").replace("]", "");
        formData.append(field, select.value);
      });

    try {
      const response = await fetch(this.dryRunUrlValue, {
        method: "POST",
        body: formData,
        headers: { "X-CSRF-Token": this.csrfToken() },
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);

      this.renderDryRun(data);
    } catch (error) {
      this.dryRunLoadingTarget.textContent = error.message;
    }
  }

  renderDryRun(data) {
    const t = this.translationsValue;
    const tones = {
      create: "success",
      link: "info",
      skip: "neutral",
      error: "danger",
    };

    this.dryRunCountersTarget.innerHTML = Object.keys(tones)
      .map(
        (key) =>
          `<span class="ds-chip ds-chip--${tones[key]}">${data.summary[key]} ${
            t[`dry_run_${key}`] || key
          }</span>`,
      )
      .join("");

    this.dryRunRowsTarget.innerHTML = data.rows
      .map((row) => this.dryRunRow(row, tones, t))
      .join("");

    this.summaryParentsCreatedTarget.textContent = data.summary.create;
    this.summaryParentsLinkedTarget.textContent =
      data.summary.link + data.summary.skip;
    this.summaryLinksCreatedTarget.textContent = data.summary.links_created;

    this.dryRunLoadingTarget.classList.add("hidden");
    this.dryRunResultTarget.classList.remove("hidden");
  }

  dryRunRow(row, tones, t) {
    const detail =
      row.status === "error"
        ? this.escapeHtml((row.messages || []).join(" — "))
        : t[`dry_run_${row.status}_detail`] || "";

    // Une ligne refusée ne sera pas traitée : annoncer « sera rattaché » à
    // côté d'une erreur promet un rattachement qui n'aura jamais lieu.
    const children =
      row.status === "error"
        ? '<span style="color:var(--ds-text-4);">—</span>'
        : (row.children || []).length
          ? row.children
              .map(
                (child) =>
                  `${this.escapeHtml(child.name)} <span style="color:var(--ds-text-4);">(${
                    child.already_linked ? t.child_linked : t.child_new
                  })</span>`,
              )
              .join("<br>")
          : `<span style="color:var(--ds-text-4);">${t.no_child}</span>`;

    const relationship = row.relationship
      ? `<div style="font:400 12px var(--g4s-font-sans); color:var(--ds-text-3);">${this.escapeHtml(
          row.relationship,
        )}</div>`
      : "";

    return `<tr>
      <td class="ds-recentrow__detail">${row.row}</td>
      <td>${this.escapeHtml(row.label) || "-"}${relationship}</td>
      <td><span class="ds-chip ds-chip--${tones[row.status]}">${
        t[`dry_run_${row.status}`] || row.status
      }</span><div style="font:400 12px var(--g4s-font-sans); color:var(--ds-text-3);">${detail}</div></td>
      <td class="ds-recentrow__detail">${children}</td>
    </tr>`;
  }

  // Les libellés viennent des cellules du fichier : un nom contenant du HTML
  // s'exécuterait dans le navigateur de la personne qui prépare l'import.
  escapeHtml(value) {
    return String(value ?? "").replace(
      /[&<>"']/g,
      (character) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[character],
    );
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : "";
  }
}
